import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import EditCustomerModal from './EditCustomerModal';
import { customerService } from '../../../services/customerService';

const CustomerProfile = ({ customer, onClose, onUpdate, canEdit = true, canDelete = true }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [localAvatar, setLocalAvatar] = useState(customer?.avatar || null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (customer?.id) {
      loadProjects();
    }
  }, [customer?.id]);

  const loadProjects = async () => {
    setProjectsLoading(true);
    try {
      const data = await customerService?.getProjectsByCustomerId(customer?.id);
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading customer projects:', err);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    onUpdate?.();
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await customerService?.delete(customer?.id);
      onUpdate?.();
      onClose?.();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete customer');
      setDeleteLoading(false);
    }
  };

  const handleAddProject = () => {
    const event = new CustomEvent('openProjectModal', {
      detail: { customerId: customer?.id, customerName: customer?.name }
    });
    window.dispatchEvent(event);
  };

  const handleNavigateToProject = (projectId) => {
    const event = new CustomEvent('openProjectDetails', {
      detail: { projectId }
    });
    window.dispatchEvent(event);
    onClose?.();
  };

  const handleAvatarUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError('');
    try {
      const updated = await customerService?.uploadAvatar(customer?.id, file);
      setLocalAvatar(updated?.avatar || null);
      onUpdate?.();
    } catch (err) {
      setAvatarError(err?.message || 'Failed to upload photo');
      setTimeout(() => setAvatarError(''), 4000);
    } finally {
      setAvatarUploading(false);
      if (fileInputRef?.current) fileInputRef.current.value = '';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      inquiry: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      planning: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      permits: 'bg-warning/10 text-warning',
      construction: 'bg-accent/10 text-accent',
      completed: 'bg-success/10 text-success'
    };
    return colors?.[status] || colors?.inquiry;
  };

  const getPhaseColor = (phase) => {
    const colors = {
      planning: 'bg-purple-100 text-purple-800',
      permits: 'bg-yellow-100 text-yellow-800',
      foundation: 'bg-orange-100 text-orange-800',
      framing: 'bg-blue-100 text-blue-800',
      systems: 'bg-indigo-100 text-indigo-800',
      finishing: 'bg-teal-100 text-teal-800',
      completed: 'bg-green-100 text-green-800',
    };
    return colors?.[phase] || 'bg-gray-100 text-gray-800';
  };

  const getAduTypeLabel = (type) => {
    const labels = {
      detached: 'Detached ADU',
      attached: 'Attached ADU',
      garage_conversion: 'Garage Conversion',
      basement_conversion: 'Basement Conversion',
      junior_adu: 'Junior ADU',
    };
    return labels?.[type] || type;
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: 'User' },
    { id: 'projects', label: `Projects (${projects?.length})`, icon: 'Briefcase' },
    { id: 'communications', label: 'Communications', icon: 'MessageSquare' },
    { id: 'documents', label: 'Documents', icon: 'FileText' }
  ];

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-card rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-4xl flex flex-col"
        style={{ maxHeight: '92vh', paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Drag handle on mobile */}
        <div className="w-10 h-1 bg-border rounded-full mx-auto mt-3 mb-1 sm:hidden flex-shrink-0" />

        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-border flex-shrink-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="relative w-14 h-14 sm:w-20 sm:h-20 flex-shrink-0 group">
                <div className="w-full h-full rounded-full overflow-hidden bg-muted">
                  <Image
                    src={localAvatar || customer?.avatar}
                    alt={customer?.name ? `${customer?.name} profile photo` : 'Customer profile photo'}
                    className="w-full h-full object-cover"
                  />
                </div>
                {canEdit && (
                  <>
                    <button
                      onClick={() => fileInputRef?.current?.click()}
                      disabled={avatarUploading}
                      className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      title="Upload photo"
                    >
                      {avatarUploading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Icon name="Camera" size={16} className="text-white" />
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleAvatarUpload}
                    />
                  </>
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-semibold text-foreground truncate">{customer?.name}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">{customer?.email}</p>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium mt-1.5 ${getStatusColor(customer?.projectStatus)}`}>
                  {customer?.projectStatus
                    ? customer?.projectStatus?.charAt(0)?.toUpperCase() + customer?.projectStatus?.slice(1)
                    : 'Inquiry'}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center flex-shrink-0"
            >
              <Icon name="X" size={22} />
            </button>
          </div>
          {avatarError && (
            <div className="mb-3 px-3 py-2 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg">
              {avatarError}
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" iconName="Edit" iconPosition="left" onClick={() => setShowEditModal(true)}>
                Edit
              </Button>
            )}
            <Button variant="outline" size="sm" iconName="Phone" iconPosition="left"
              onClick={() => customer?.phone && window.open(`tel:${customer?.phone}`)}
            >
              Call
            </Button>
            <Button variant="outline" size="sm" iconName="Mail" iconPosition="left"
              onClick={() => customer?.email && window.open(`mailto:${customer?.email}`)}
            >
              Email
            </Button>
            <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddProject}>
              New Project
            </Button>
            {canDelete && (
              <Button
                variant="outline"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Tabs - scrollable on mobile */}
        <div className="border-b border-border flex-shrink-0">
          <div className="tabs-scroll-container px-4 sm:px-6 gap-0">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 min-h-[44px] ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={14} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="text-foreground mt-1">{customer?.phone || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="text-foreground mt-1">{customer?.email || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Lead Source</label>
                    <p className="text-foreground mt-1 capitalize">{customer?.leadSource?.replace(/_/g, ' ') || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Contact</label>
                    <p className="text-foreground mt-1">{formatDate(customer?.lastContact)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Property Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Property Address</label>
                    <p className="text-foreground mt-1">{customer?.propertyAddress || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">ADU Type</label>
                    <p className="text-foreground mt-1">{getAduTypeLabel(customer?.aduType)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Zoning Status</label>
                    <p className="text-foreground mt-1">{customer?.zoningStatus || '—'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Estimated Budget</label>
                    <p className="text-foreground mt-1">
                      {customer?.estimatedBudget ? `$${Number(customer?.estimatedBudget)?.toLocaleString()}` : '—'}
                    </p>
                  </div>
                </div>
              </div>

              {customer?.projectPreferences && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Project Preferences</h3>
                  <p className="text-foreground">{customer?.projectPreferences}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Project History</h3>
                <Button variant="default" size="sm" iconName="Plus" iconPosition="left" onClick={handleAddProject}>
                  New Project
                </Button>
              </div>

              {projectsLoading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading projects...</p>
                </div>
              ) : projects?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Briefcase" size={48} className="mx-auto mb-3 opacity-50" />
                  <p className="font-medium mb-1">No projects yet</p>
                  <p className="text-sm">Click "New Project" to create the first project for this customer.</p>
                </div>
              ) : (
                projects?.map((project) => (
                  <button
                    key={project?.id}
                    onClick={() => handleNavigateToProject(project?.id)}
                    className="w-full bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors text-left"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">{project?.address}</h4>
                        <p className="text-sm text-muted-foreground">{getAduTypeLabel(project?.aduType)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize ${getPhaseColor(project?.phase)}`}>
                          {project?.phase?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-primary flex items-center gap-1">
                          <Icon name="ExternalLink" size={12} />
                          View Details
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Budget</p>
                        <p className="font-medium text-foreground">
                          {project?.budgetTotal ? `$${Number(project?.budgetTotal)?.toLocaleString()}` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Started</p>
                        <p className="font-medium text-foreground">{formatDate(project?.createdAt)}</p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Communication Timeline</h3>
              </div>
              <div className="text-center py-12 text-muted-foreground">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="MessageSquare" size={32} className="opacity-50" />
                </div>
                <p className="font-medium mb-1">No communications logged</p>
                <p className="text-sm">Communication history will appear here once calls, emails, or notes are recorded.</p>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Documents</h3>
              </div>
              <div className="text-center py-12 text-muted-foreground">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="FileText" size={32} className="opacity-50" />
                </div>
                <p className="font-medium mb-1">No documents uploaded</p>
                <p className="text-sm">Customer documents and files will appear here once uploaded.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Edit Modal */}
      {showEditModal && (
        <EditCustomerModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
          customer={customer}
        />
      )}
      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={20} className="text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Delete Customer</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-foreground mb-2">
              Are you sure you want to delete <strong>{customer?.name}</strong>?
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              All associated projects and data will also be permanently deleted.
            </p>
            {deleteError && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded mb-4 text-sm">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteLoading}
                iconName={deleteLoading ? undefined : 'Trash2'}
                iconPosition="left"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Customer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;